import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Escape HTML to prevent XSS when interpolating user-controlled
 * strings into HTML templates (e.g. printable receipts).
 */
export function escapeHtml(value: unknown): string {
  if (value === null || value === undefined) return '';
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

/**
 * Format currency with flexible locale support
 */
export function formatCurrency(amount: number, options?: { showDecimals?: boolean; currency?: string }): string {
  const { showDecimals = true, currency = 'USD' } = options || {};
  return `$${amount.toLocaleString(undefined, { 
    minimumFractionDigits: showDecimals ? 2 : 0, 
    maximumFractionDigits: showDecimals ? 2 : 0 
  })}`;
}

/**
 * Format phone number for display (international-friendly)
 * Returns the phone number as-is if already formatted, or cleans up whitespace
 */
export function formatPhoneNumber(value: string | null | undefined): string {
  if (!value) return '';
  return value.trim();
}

/**
 * Format phone number for display (read-only)
 * Returns formatted string or empty string if invalid
 */
export function displayPhoneNumber(phone: string | null | undefined): string {
  if (!phone) return '';
  return phone.trim();
}

/**
 * Format a complete business address with street address, state/region, and postcode
 */
export function formatFullAddress(
  address?: string | null,
  state?: string | null,
  postcode?: string | null
): string {
  return [address, state, postcode].filter(Boolean).join(', ');
}

/**
 * Format a tax identification number (e.g. ABN, TIN, EIN)
 * Formats as XX XXX XXX XXX by default
 */
export function formatABN(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 11);
  if (digits.length <= 2) return digits;
  if (digits.length <= 5) return `${digits.slice(0, 2)} ${digits.slice(2)}`;
  if (digits.length <= 8) return `${digits.slice(0, 2)} ${digits.slice(2, 5)} ${digits.slice(5)}`;
  return `${digits.slice(0, 2)} ${digits.slice(2, 5)} ${digits.slice(5, 8)} ${digits.slice(8, 11)}`;
}

/**
 * Common regions/states for dropdown (generic, user can type their own)
 * Kept as a legacy export name for compatibility
 */
export const AUSTRALIAN_STATES: { value: string; label: string }[] = [];

/**
 * Get timezone for a region/state (defaults to UTC if unknown)
 */
export function getTimezoneForState(state: string | null | undefined): string {
  if (!state) return "UTC";
  // Common timezone mappings can be extended as needed
  return "UTC";
}

/**
 * Get timezone offset string for a date in a specific timezone
 * Uses Intl.DateTimeFormat to correctly handle DST
 */
export function getTimezoneOffset(date: Date, tz: string): string {
  try {
    const formatter = new Intl.DateTimeFormat('en', {
      timeZone: tz,
      timeZoneName: 'longOffset'
    });
    const parts = formatter.formatToParts(date);
    const offsetPart = parts.find(p => p.type === 'timeZoneName');
    const offset = offsetPart?.value.replace('GMT', '') || '+00:00';
    return offset;
  } catch (error) {
    console.error('Error getting timezone offset:', error);
    return '+00:00';
  }
}
